/**
 * Support.
 *
 * A restyle, not a rebuild. The data layer already matched
 * taskdojo-app/src/screens/SupportChatScreen.js field for field -- the same
 * `supportChats/support_{uid}` document, the same participants array, the same
 * message shape (senderId, senderName, senderEmail, text, createdAt) and the
 * same lastMessage/lastMessageAt preview. That is left exactly as it was; only
 * the presentation is new.
 *
 * One thing was fixed while here: timestamps were formatted with
 * `toLocaleTimeString([], …)`, an unpinned locale, which is the bug this
 * codebase keeps re-introducing. They go through utils/datetime like
 * everything else now.
 *
 * Unread counts are the Cloud Function's job, not ours; opening the screen only
 * clears `unreadByUser`, which is what makes the dot in the nav go away.
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  collection, doc, setDoc, addDoc, onSnapshot, query, orderBy, updateDoc,
  getDocs, where, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { useApp } from '../../contexts/AppContext';
import { ScreenHeader, PrimaryButton, useInputClass } from '../dojo';
import { formatShortDateTime } from '../../utils/datetime';

interface ChatMessage {
  id: string;
  senderId: string;
  text: string;
  createdAt: any;
}

const ADMIN_EMAIL = 'christos@splat.sh';

const SupportChat: React.FC = () => {
  const { colors, isDark } = useTheme();
  const { state } = useApp();
  const input = useInputClass();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adminUid, setAdminUid] = useState<string | null>(null);
  const [error, setError] = useState('');

  const endRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLTextAreaElement>(null);

  const userId = state.user?.uid || '';
  const userEmail = state.user?.email || '';
  const userName = state.user?.displayName || '';
  const chatDocId = `support_${userId}`;

  // Grow with the message, to a limit.
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [inputText]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  useEffect(() => {
    if (!userId) return;

    const initChat = async () => {
      try {
        const snap = await getDocs(
          query(collection(db, 'users'), where('email', '==', ADMIN_EMAIL)),
        );
        if (snap.empty) { setLoading(false); return; }
        const foundAdminUid = snap.docs[0].id;
        setAdminUid(foundAdminUid);

        // setDoc/merge rather than getDoc: the rules refuse a read of a
        // document that does not exist yet, so checking first fails.
        await setDoc(doc(db, 'supportChats', chatDocId), {
          userId,
          userEmail,
          userName,
          adminUid: foundAdminUid,
          participants: [userId, foundAdminUid],
          createdAt: serverTimestamp(),
          unreadByUser: 0,
        }, { merge: true });

        setLoading(false);
      } catch (err: any) {
        setError(err?.message || "Couldn't open the support chat.");
        setLoading(false);
      }
    };

    initChat();
  }, [userId, userEmail, userName, chatDocId]);

  useEffect(() => {
    if (!userId) return;
    const q = query(
      collection(db, 'supportChats', chatDocId, 'messages'),
      orderBy('createdAt', 'asc'),
    );
    const unsub = onSnapshot(q, (snapshot) => {
      setMessages(snapshot.docs.map((d) => ({
        id: d.id,
        senderId: d.data().senderId,
        text: d.data().text,
        createdAt: d.data().createdAt,
      })));
    }, (e) => setError(e?.message || "Couldn't load the conversation."));
    return () => unsub();
  }, [userId, chatDocId]);

  // Clears the dot in the nav.
  useEffect(() => {
    if (!userId) return;
    updateDoc(doc(db, 'supportChats', chatDocId), { unreadByUser: 0 }).catch(() => {});
  }, [messages, userId, chatDocId]);

  const handleSend = async () => {
    if (!inputText.trim() || sending || !adminUid) return;
    const text = inputText.trim();
    setInputText('');
    setSending(true);
    setError('');
    try {
      await addDoc(collection(db, 'supportChats', chatDocId, 'messages'), {
        senderId: userId,
        senderName: userName || 'User',
        senderEmail: userEmail,
        text,
        createdAt: serverTimestamp(),
      });
      // The preview on the parent doc. The unread count is the Cloud
      // Function's to maintain, deliberately not ours.
      await updateDoc(doc(db, 'supportChats', chatDocId), {
        lastMessage: text,
        lastMessageAt: serverTimestamp(),
      });
    } catch (err: any) {
      setError(err?.message || "Couldn't send that. Please try again.");
      setInputText(text);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={`min-h-screen flex flex-col ${colors.bg} ${colors.text}`}>
      <div className="max-w-2xl w-full mx-auto flex-1 flex flex-col pt-4 pb-28">
        <ScreenHeader title="Chris" subtitle="taskDojo founder" />

        {error && (
          <div role="alert" className="mx-5 mb-3 rounded-[14px] border-2 border-dojo-red/50 bg-dojo-red/10
            px-4 py-3 text-[13px] font-semibold text-dojo-red">
            {error}
          </div>
        )}

        <div className="flex-1 px-5 space-y-3">
          {loading ? (
            <p className={`text-[14px] font-semibold ${colors.textSecondary}`}>Loading…</p>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center text-center py-16">
              <div className={`w-[88px] h-[88px] rounded-[30px] border-2 ${colors.cardBorder} ${colors.card}
                flex items-center justify-center mb-[18px] text-[34px]`}>💬</div>
              <h2 className="text-xl font-black tracking-[-0.4px] mb-1.5">Say hello</h2>
              <p className={`text-[15px] font-semibold ${colors.textSecondary}`}>
                Questions, bugs, or ideas — this goes straight to Chris.
              </p>
            </div>
          ) : (
            messages.map((m) => {
              const mine = m.senderId === userId;
              return (
                <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                  <div className="max-w-[80%]">
                    <div
                      className={`rounded-[15px] px-4 py-3 text-[14.5px] leading-relaxed border-2 border-b-[3px]
                        ${mine
                          ? 'bg-dojo-red border-dojo-redDeep text-white'
                          : `${colors.card} ${colors.cardBorder}`}`}
                    >
                      <p className="whitespace-pre-wrap break-words">{m.text}</p>
                    </div>
                    <p className={`text-[11px] font-semibold mt-1 ${colors.textMuted} ${mine ? 'text-right' : ''}`}>
                      {formatShortDateTime(m.createdAt)}
                    </p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={endRef} />
        </div>
      </div>

      <div className={`fixed bottom-0 left-0 right-0 lg:left-64 z-30 border-t-2
        ${colors.cardBorder} ${colors.card} pb-20 lg:pb-0`}>
        <div className="max-w-2xl mx-auto flex gap-2 p-3 items-end">
          <textarea
            ref={boxRef}
            className={`${input} flex-1 resize-none min-h-[46px] py-3`}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={(e) => {
              // Enter sends, shift+enter breaks the line -- the convention
              // every chat on a keyboard uses.
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
            }}
            placeholder="Write a message…"
            rows={1}
            disabled={loading}
          />
          <PrimaryButton
            onClick={handleSend}
            disabled={sending || !inputText.trim() || !adminUid}
          >
            {sending ? '…' : 'Send'}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export default SupportChat;
